import React, { useState, useEffect } from 'react';
import { Feature, Component, TestCase } from '../../../types';

interface FeatureTreeSidebarProps {
  features: Feature[];
  components: Component[];
  testCases: TestCase[];
  selectedFeatureIds: Set<string>;
  selectedComponentIds: Set<string>;
  activeComponentId: string | null;
  onFeatureSelect: (featureId: string, selected: boolean) => void;
  onComponentSelect: (componentId: string, selected: boolean) => void;
  onComponentClick: (componentId: string) => void;
  searchText: string;
  onSearchChange: (text: string) => void;
  onComponentDropOnFeature?: (componentId: string, targetFeatureId: string) => void;
  onTestCaseDropOnComponent?: (testCaseId: string, targetComponentId: string) => void;
}

const FeatureTreeSidebar: React.FC<FeatureTreeSidebarProps> = ({
  features,
  components,
  testCases,
  selectedFeatureIds,
  selectedComponentIds,
  activeComponentId,
  onFeatureSelect,
  onComponentSelect,
  onComponentClick,
  searchText,
  onSearchChange,
  onComponentDropOnFeature,
  onTestCaseDropOnComponent
}) => {
  const [expandedFeatures, setExpandedFeatures] = useState<Set<string>>(new Set());
  const [dragOverFeatureId, setDragOverFeatureId] = useState<string | null>(null);
  const [dragOverComponentId, setDragOverComponentId] = useState<string | null>(null);

  // Expand all features by default when features load/change
  useEffect(() => {
    setExpandedFeatures(new Set(features.map(f => f.id)));
  }, [features]);

  const toggleFeatureExpanded = (featureId: string) => {
    setExpandedFeatures(prev => {
      const next = new Set(prev);
      if (next.has(featureId)) {
        next.delete(featureId);
      } else {
        next.add(featureId);
      }
      return next;
    });
  };

  const getComponentsForFeature = (featureId: string): Component[] => {
    return components.filter(c => c.featureId === featureId);
  };

  const getTestCaseCountForComponent = (componentId: string): number => {
    return testCases.filter(tc => tc.componentId === componentId).length;
  };

  const getTotalTestCaseCountForFeature = (featureId: string): number => {
    const featureComponents = getComponentsForFeature(featureId);
    return featureComponents.reduce((sum, c) => sum + getTestCaseCountForComponent(c.id), 0);
  };

  // Filter features based on search
  const filteredFeatures = features.filter(feature => {
    if (!searchText.trim()) return true;
    
    const search = searchText.toLowerCase();
    
    // Check feature name
    if (feature.name.toLowerCase().includes(search)) return true;
    
    // Check components in this feature
    const featureComponents = getComponentsForFeature(feature.id);
    if (featureComponents.some(c => c.name.toLowerCase().includes(search))) return true;
    
    // Check test cases
    const featureTestCases = testCases.filter(tc => 
      featureComponents.some(c => c.id === tc.componentId)
    );
    if (featureTestCases.some(tc => tc.title.toLowerCase().includes(search))) return true;
    
    return false;
  });

  // Drag handlers for components
  const handleComponentDragStart = (e: React.DragEvent, componentId: string) => {
    e.dataTransfer.setData('componentId', componentId);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Feature as drop target for components
  const handleFeatureDragOver = (e: React.DragEvent, featureId: string) => {
    const componentId = e.dataTransfer.types.includes('componentid');
    if (componentId) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDragOverFeatureId(featureId);
    }
  };

  const handleFeatureDragLeave = () => {
    setDragOverFeatureId(null);
  };

  const handleFeatureDrop = (e: React.DragEvent, targetFeatureId: string) => {
    e.preventDefault();
    const componentId = e.dataTransfer.getData('componentId');
    if (componentId && onComponentDropOnFeature) {
      onComponentDropOnFeature(componentId, targetFeatureId);
    }
    setDragOverFeatureId(null);
  };

  // Component as drop target for test cases
  const handleComponentDragOver = (e: React.DragEvent, componentId: string) => {
    const hasTestCase = e.dataTransfer.types.includes('testcaseid');
    if (hasTestCase) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDragOverComponentId(componentId);
    }
  };

  const handleComponentDragLeave = () => {
    setDragOverComponentId(null);
  };

  const handleComponentDrop = (e: React.DragEvent, targetComponentId: string) => {
    e.preventDefault();
    const testCaseId = e.dataTransfer.getData('testCaseId');
    if (testCaseId && onTestCaseDropOnComponent) {
      onTestCaseDropOnComponent(testCaseId, targetComponentId);
    }
    setDragOverComponentId(null);
  };

  return (
    <div className="library-sidebar">
      {/* Search */}
      <div className="library-sidebar-search">
        <input
          type="text"
          placeholder="Search features, components..."
          value={searchText}
          onChange={(e) => onSearchChange(e.target.value)}
          className="input"
        />
      </div>

      {/* Features Header */}
      <div className="library-sidebar-header">
        <span className="library-sidebar-title">Features</span>
      </div>

      {/* Feature Tree */}
      <div className="library-sidebar-list">
        {filteredFeatures.length === 0 ? (
          <div className="library-detail-empty" style={{ padding: '2rem 1rem' }}>
            <p>{searchText ? 'No matches found' : 'No features yet'}</p>
          </div>
        ) : (
          filteredFeatures.map(feature => {
            const featureComponents = getComponentsForFeature(feature.id);
            const isExpanded = expandedFeatures.has(feature.id);
            const isFeatureSelected = selectedFeatureIds.has(feature.id);
            const totalTestCases = getTotalTestCaseCountForFeature(feature.id);
            const isDragOver = dragOverFeatureId === feature.id;

            return (
              <div key={feature.id} className="library-tree-item">
                {/* Feature Row - Drop target for components */}
                <div 
                  className={`library-tree-row ${isFeatureSelected ? 'selected' : ''} ${isDragOver ? 'drag-over' : ''}`}
                  onClick={() => toggleFeatureExpanded(feature.id)}
                  onDragOver={(e) => handleFeatureDragOver(e, feature.id)}
                  onDragLeave={handleFeatureDragLeave}
                  onDrop={(e) => handleFeatureDrop(e, feature.id)}
                >
                  <input
                    type="checkbox"
                    checked={isFeatureSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      onFeatureSelect(feature.id, e.target.checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="library-tree-expand">
                    {isExpanded ? '▼' : '▶'}
                  </span>
                  <span className="library-tree-name">{feature.name}</span>
                  <span className="library-tree-count">{totalTestCases}</span>
                </div>

                {/* Components (when expanded) */}
                {isExpanded && (
                  <div className="library-tree-children">
                    {featureComponents.map(component => {
                      const isComponentSelected = selectedComponentIds.has(component.id);
                      const isActive = activeComponentId === component.id;
                      const testCaseCount = getTestCaseCountForComponent(component.id);
                      const isComponentDragOver = dragOverComponentId === component.id;

                      return (
                        <div
                          key={component.id}
                          className={`library-tree-row library-tree-child ${isActive ? 'active' : ''} ${isComponentSelected ? 'selected' : ''} ${isComponentDragOver ? 'drag-over' : ''}`}
                          onClick={() => onComponentClick(component.id)}
                          draggable
                          onDragStart={(e) => handleComponentDragStart(e, component.id)}
                          onDragOver={(e) => handleComponentDragOver(e, component.id)}
                          onDragLeave={handleComponentDragLeave}
                          onDrop={(e) => handleComponentDrop(e, component.id)}
                        >
                          <input
                            type="checkbox"
                            checked={isComponentSelected}
                            onChange={(e) => {
                              e.stopPropagation();
                              onComponentSelect(component.id, e.target.checked);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="library-tree-name">{component.name}</span>
                          <span className="library-tree-count">{testCaseCount}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default FeatureTreeSidebar;
